import axios from "axios";
import {
	clearAuthSession,
	getAccessToken,
	getRefreshToken,
	isAccessTokenExpiringSoon,
	updateAccessToken,
} from "./authStorage";

const BASE_URL = "https://recon.jcinformatics.com/";

const AUTH_MESSAGE_SNIPPETS = [
	"sesion no verificada",
	"token invalido",
	"sesion expirada",
	"token no proporcionado",
	"missing refreshtoken",
	"invalid refreshtoken",
];

const normalizeText = (value) => {
	if (typeof value !== "string") return "";

	return value
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();
};

const looksLikeAuthMessage = (value) => {
	const normalized = normalizeText(value);
	if (!normalized) return false;

	return AUTH_MESSAGE_SNIPPETS.some((snippet) => normalized.includes(snippet));
};

const isAuthPayload = (payload) => {
	if (!payload || typeof payload !== "object") return false;
	if (payload.success === true) return false;

	return looksLikeAuthMessage(payload.message) || looksLikeAuthMessage(payload.error);
};

const createAuthError = (response) => {
	const authError = new Error(
		response?.data?.message || response?.data?.error || "Sesion no valida"
	);

	authError.config = response?.config || {};
	authError.response = {
		...response,
		status: 401,
		data: response?.data,
	};

	return authError;
};

const isAuthEndpoint = (url = "") => {
	const normalizedUrl = String(url).toLowerCase();

	return (
		normalizedUrl.includes("login.php") || normalizedUrl.includes("refresh-token.php")
	);
};

let refreshPromise = null;

const performRefresh = async () => {
	const refreshToken = getRefreshToken();
	if (!refreshToken) {
		throw new Error("No hay refresh token disponible");
	}

	const refreshResp = await axios.post(
		`${BASE_URL}refresh-token.php`,
		{ refreshToken },
		{ headers: { "Content-Type": "application/json" } }
	);

	const newAccessToken = refreshResp.data?.accessToken;
	if (!newAccessToken) {
		throw new Error("Refresh sin accessToken");
	}

	return updateAccessToken({
		accessToken: newAccessToken,
		refreshToken: refreshResp.data?.refreshToken,
		expiresIn: refreshResp.data?.expiresIn,
	});
};

export const refreshAccessToken = async () => {
	if (!refreshPromise) {
		refreshPromise = performRefresh()
			.catch((error) => {
				clearAuthSession();
				throw error;
			})
			.finally(() => {
				refreshPromise = null;
			});
	}

	return refreshPromise;
};

export const ensureValidAccessToken = async ({ forceRefresh = false } = {}) => {
	const accessToken = getAccessToken();
	const refreshToken = getRefreshToken();

	if (!refreshToken) {
		return accessToken;
	}

	if (forceRefresh || isAccessTokenExpiringSoon()) {
		return refreshAccessToken();
	}

	return accessToken;
};

export const api = axios.create({
	baseURL: BASE_URL,
	timeout: 20000,
});

api.interceptors.request.use(
	async (config) => {
		if (isAuthEndpoint(config?.url)) {
			return config;
		}

		const token = await ensureValidAccessToken();
		if (token) {
			config.headers = config.headers || {};
			config.headers.Authorization = `Bearer ${token}`;
		}

		return config;
	},
	(error) => Promise.reject(error)
);

api.interceptors.response.use(
	(response) => {
		if (isAuthPayload(response?.data)) {
			return Promise.reject(createAuthError(response));
		}

		return response;
	},
	async (error) => {
		const originalRequest = error?.config || {};
		const status = error?.response?.status;
		const payload = error?.response?.data;

		const shouldRetry =
			!isAuthEndpoint(originalRequest?.url) &&
			!originalRequest._retry &&
			(status === 401 || isAuthPayload(payload));

		if (!shouldRetry) {
			return Promise.reject(error);
		}

		if (!getRefreshToken()) {
			clearAuthSession();
			return Promise.reject(error);
		}

		originalRequest._retry = true;

		try {
			const newToken = await refreshAccessToken();
			originalRequest.headers = originalRequest.headers || {};
			originalRequest.headers.Authorization = `Bearer ${newToken}`;
			return api(originalRequest);
		} catch (refreshError) {
			clearAuthSession();
			return Promise.reject(refreshError);
		}
	}
);

export const authFetch = async (input, init = {}, options = {}) => {
	const retryStatuses =
		Array.isArray(options.retryStatuses) && options.retryStatuses.length > 0
			? options.retryStatuses
			: [401, 403];

	const buildRequestInit = (token) => {
		const headers = new Headers(init.headers || {});

		if (token) {
			headers.set("Authorization", `Bearer ${token}`);
		}

		return {
			...init,
			headers,
		};
	};

	const initialToken = await ensureValidAccessToken();
	let response = await fetch(input, buildRequestInit(initialToken));

	if (!retryStatuses.includes(response.status) || !getRefreshToken()) {
		return response;
	}

	const refreshedToken = await refreshAccessToken();
	response = await fetch(input, buildRequestInit(refreshedToken));
	return response;
};
